/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 28th 2024, 5:01:49 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import create from "zustand";
import { MediaLibrary, MediaLibraryImage, MediaLibraryFile } from "lib/schemas";
import { addOrUpdateItem, removeItem } from "lib/utils";

/** The Store */
interface IMediaLibraryStore {
  items: {
    id: MediaLibrary["id"];
    mediaLibrary: MediaLibrary;
    mediaLibraryImages: MediaLibraryImage[];
    mediaLibraryFiles: MediaLibraryFile[];
  }[];
  isPopulated: boolean;
  populateMediaLibraries: (mediaLibraries: MediaLibrary[]) => void;
  clearMediaLibraries: () => void;
  addMediaLibrary: (mediaLibrary: MediaLibrary) => void;
  removeMediaLibrary: (mediaLibraryId: MediaLibrary["id"]) => void;
  populateImages: (mediaLibraryId: MediaLibrary["id"], images: MediaLibraryImage[]) => void;
  addImage: (mediaLibraryId: MediaLibrary["id"], image: MediaLibraryImage) => void;
  removeImage: (mediaLibraryId: MediaLibrary["id"], imageId: MediaLibraryImage["id"]) => void;
  populateFiles: (mediaLibraryId: MediaLibrary["id"], files: MediaLibraryFile[]) => void;
  addFile: (mediaLibraryId: MediaLibrary["id"], file: MediaLibraryFile) => void;
  removeFile: (mediaLibraryId: MediaLibrary["id"], fileId) => void;
}

const mediaLibraryInit = (mediaLibrary: MediaLibrary) => ({
  id: mediaLibrary.id,
  mediaLibrary,
  mediaLibraryFiles: [],
  mediaLibraryImages: [],
});

export const useMediaLibraryStore = create<IMediaLibraryStore>((set) => {
  return {
    items: [],
    isPopulated: false,
    populateMediaLibraries: (mediaLibraries) =>
      set((state) => ({
        items: mediaLibraries.map(mediaLibraryInit),
        isPopulated: true,
      })),
    clearMediaLibraries: () => set((state) => ({ items: [], isPopulated: false })),
    addMediaLibrary: (newMediaLibrary) =>
      set((state) => ({
        items: addOrUpdateItem(state.items, mediaLibraryInit(newMediaLibrary), "id"),
      })),
    removeMediaLibrary: (mediaLibraryId) =>
      set((state) => ({ items: state.items.filter((item) => item.mediaLibrary.id !== mediaLibraryId) })),
    populateImages: (mediaLibraryId, images) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return { ...item, mediaLibraryImages: images };
          }
          return item;
        }),
      }));
    },
    addImage: (mediaLibraryId, image) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return {
              ...item,
              mediaLibraryImages: addOrUpdateItem(item.mediaLibraryImages, image, "id", null, {
                addOperation: "prepend",
              }),
            };
          }
          return item;
        }),
      }));
    },
    removeImage: (mediaLibraryId, imageId) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return { ...item, mediaLibraryImages: removeItem(item.mediaLibraryImages, imageId, "id") };
          }
          return item;
        }),
      }));
    },
    populateFiles: (mediaLibraryId, files) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return { ...item, mediaLibraryFiles: files };
          }
          return item;
        }),
      }));
    },
    addFile: (mediaLibraryId, file) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return {
              ...item,
              mediaLibraryFiles: addOrUpdateItem(item.mediaLibraryFiles, file, "id", null, { addOperation: "prepend" }),
            };
          }
          return item;
        }),
      }));
    },
    removeFile: (mediaLibraryId, fileId) => {
      set((state) => ({
        items: state.items.map((item) => {
          if (item.mediaLibrary.id === mediaLibraryId) {
            return { ...item, mediaLibraryFiles: removeItem(item.mediaLibraryFiles, fileId, "id") };
          }
          return item;
        }),
      }));
    },
  };
});

/** Selectors */
export const mediaLibraryStoreSelectors = {
  getLibraries: (organizationId: MediaLibrary["organization"]) => (state: IMediaLibraryStore) =>
    state.items.filter((item) => item.mediaLibrary.organization === organizationId),

  getLibrary: (libraryId: MediaLibrary["id"]) => (state: IMediaLibraryStore) =>
    state.items.find((item) => item.mediaLibrary.id === libraryId),
};
